// StockIssueForm.jsx
import React, { useState, useEffect } from 'react';

const StockIssueForm = ({ onSubmit, initialData = null, isLoading = false }) => {
  // Form state
  const [formData, setFormData] = useState({
    // Common fields
    medicine_id: '',
    quantity: '',
    remarks: '',
    department: '',
    reference_type: 'consultation',
    purpose: '',
    diagnosis: '',
    doctor_notes: '',
    
    // Conditional fields
    patient_id: '',
    employee_id: '',
    visit_id: '',
    prescription_id: '',
    authorized_by: '',
    
    // Controlled medicine flag
    isControlled: false
  });

  const [errors, setErrors] = useState({});
  const [issueType, setIssueType] = useState('patient');
  const [medicines, setMedicines] = useState([]);
  const [patients, setPatients] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [visits, setVisits] = useState([]);
  const [availableStock, setAvailableStock] = useState(null);
  const [loadingMedicines, setLoadingMedicines] = useState(false);

  // Load initial data if provided
  useEffect(() => {
    if (initialData) {
      setFormData(prev => ({
        ...prev,
        ...initialData
      }));
      
      if (initialData.employee_id) {
        setIssueType('employee');
      } else if (initialData.authorized_by || initialData.isControlled) {
        setIssueType('controlled');
      } else {
        setIssueType('patient');
      }
    }
  }, [initialData]);

  // Load medicines on mount
  useEffect(() => {
    fetchMedicines();
    fetchPatients();
    fetchEmployees();
  }, []);

  // Fetch visits when patient is selected
  useEffect(() => {
    if (formData.patient_id) {
      fetchPatientVisits(formData.patient_id);
    }
  }, [formData.patient_id]);

  // Check medicine stock when medicine is selected
  useEffect(() => {
    if (formData.medicine_id) {
      checkMedicineStock(formData.medicine_id);
    }
  }, [formData.medicine_id]);

  // API Functions
  const fetchMedicines = async () => {
    setLoadingMedicines(true);
    try {
      const response = await fetch('/api/medicines');
      const data = await response.json();
      setMedicines(data);
    } catch (error) {
      console.error('Error fetching medicines:', error);
    } finally {
      setLoadingMedicines(false);
    }
  };

  const fetchPatients = async () => {
    try {
      const response = await fetch('/api/patients');
      const data = await response.json();
      setPatients(data);
    } catch (error) {
      console.error('Error fetching patients:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await fetch('/api/employees');
      const data = await response.json();
      setEmployees(data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchPatientVisits = async (patientId) => {
    try {
      const response = await fetch(`/api/patients/${patientId}/visits`);
      const data = await response.json();
      setVisits(data);
    } catch (error) {
      console.error('Error fetching visits:', error);
    }
  };

  const checkMedicineStock = async (medicineId) => {
    try {
      const response = await fetch(`/api/medicines/${medicineId}/stock`);
      const data = await response.json();
      setAvailableStock(data.available_quantity);
    } catch (error) {
      console.error('Error checking stock:', error);
    }
  };

  // Validation
  const validateForm = () => {
    const newErrors = {};

    if (!formData.medicine_id) newErrors.medicine_id = 'Medicine is required';
    if (!formData.quantity) newErrors.quantity = 'Quantity is required';
    else if (availableStock && parseInt(formData.quantity) > availableStock) {
      newErrors.quantity = `Quantity exceeds available stock (${availableStock})`;
    }

    if (issueType === 'patient' || issueType === 'controlled') {
      if (!formData.patient_id) newErrors.patient_id = 'Patient is required';
      if (!formData.visit_id) newErrors.visit_id = 'Visit is required';
    }

    if (issueType === 'employee') {
      if (!formData.employee_id) newErrors.employee_id = 'Employee is required';
    }

    if (issueType === 'controlled') {
      if (!formData.authorized_by) newErrors.authorized_by = 'Authorizer is required';
      if (!formData.prescription_id) newErrors.prescription_id = 'Prescription ID is required';
    }

    if (!formData.department) newErrors.department = 'Department is required';
    if (!formData.purpose) newErrors.purpose = 'Purpose is required';
    if (!formData.diagnosis) newErrors.diagnosis = 'Diagnosis is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    let payload = {};

    switch (issueType) {
      case 'employee':
        payload = {
          medicine_id: parseInt(formData.medicine_id),
          quantity: parseInt(formData.quantity),
          employee_id: parseInt(formData.employee_id),
          department: formData.department,
          remarks: formData.remarks,
          reference_type: formData.reference_type,
          purpose: formData.purpose,
          diagnosis: formData.diagnosis,
          doctor_notes: formData.doctor_notes
        };
        break;

      case 'controlled':
        payload = {
          medicine_id: parseInt(formData.medicine_id),
          quantity: parseInt(formData.quantity),
          patient_id: parseInt(formData.patient_id),
          visit_id: parseInt(formData.visit_id),
          prescription_id: parseInt(formData.prescription_id),
          authorized_by: parseInt(formData.authorized_by),
          department: formData.department,
          remarks: formData.remarks,
          reference_type: formData.reference_type,
          purpose: formData.purpose,
          diagnosis: formData.diagnosis,
          doctor_notes: formData.doctor_notes
        };
        break;

      default:
        payload = {
          medicine_id: parseInt(formData.medicine_id),
          quantity: parseInt(formData.quantity),
          patient_id: parseInt(formData.patient_id),
          visit_id: parseInt(formData.visit_id),
          department: formData.department,
          remarks: formData.remarks,
          reference_type: formData.reference_type,
          purpose: formData.purpose,
          diagnosis: formData.diagnosis,
          doctor_notes: formData.doctor_notes
        };
    }

    if (issueType === 'patient' && formData.prescription_id) {
      payload.prescription_id = parseInt(formData.prescription_id);
    }

    onSubmit(payload);
  };

  const handleReset = () => {
    setFormData({
      medicine_id: '',
      quantity: '',
      remarks: '',
      department: '',
      reference_type: 'consultation',
      purpose: '',
      diagnosis: '',
      doctor_notes: '',
      patient_id: '',
      employee_id: '',
      visit_id: '',
      prescription_id: '',
      authorized_by: '',
      isControlled: false
    });
    setErrors({});
    setIssueType('patient');
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      <h2 className="text-2xl font-bold mb-6 text-gray-800">Stock Issue Form</h2>

      {/* Issue Type Selector */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Issue Type
        </label>
        <div className="flex space-x-4">
          <button
            type="button"
            onClick={() => setIssueType('patient')}
            className={`px-4 py-2 rounded-md ${
              issueType === 'patient'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            To Patient
          </button>
          <button
            type="button"
            onClick={() => setIssueType('employee')}
            className={`px-4 py-2 rounded-md ${
              issueType === 'employee'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            To Employee
          </button>
          <button
            type="button"
            onClick={() => setIssueType('controlled')}
            className={`px-4 py-2 rounded-md ${
              issueType === 'controlled'
                ? 'bg-red-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Controlled Medicine
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Medicine Selection */}
          <div className="col-span-2 md:col-span-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Medicine <span className="text-red-500">*</span>
            </label>
            <select
              name="medicine_id"
              value={formData.medicine_id}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-md ${
                errors.medicine_id ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <option value="">Select Medicine</option>
              {medicines.map(medicine => (
                <option key={medicine.id} value={medicine.id}>
                  {medicine.name} ({medicine.strength})
                </option>
              ))}
            </select>
            {errors.medicine_id && (
              <p className="mt-1 text-sm text-red-500">{errors.medicine_id}</p>
            )}
          </div>

          {/* Quantity */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Quantity <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              name="quantity"
              value={formData.quantity}
              onChange={handleChange}
              min="1"
              className={`w-full px-3 py-2 border rounded-md ${
                errors.quantity ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.quantity && (
              <p className="mt-1 text-sm text-red-500">{errors.quantity}</p>
            )}
            {availableStock && (
              <p className="mt-1 text-sm text-gray-500">
                Available stock: {availableStock}
              </p>
            )}
          </div>

          {/* Department */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Department <span className="text-red-500">*</span>
            </label>
            <select
              name="department"
              value={formData.department}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-md ${
                errors.department ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <option value="">Select Department</option>
              <option value="OPD">OPD</option>
              <option value="Emergency">Emergency</option>
              <option value="Clinic">Clinic</option>
              <option value="ICU">ICU</option>
              <option value="Pharmacy">Pharmacy</option>
            </select>
            {errors.department && (
              <p className="mt-1 text-sm text-red-500">{errors.department}</p>
            )}
          </div>

          {/* Conditional Fields based on Issue Type */}
          {issueType === 'patient' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Patient <span className="text-red-500">*</span>
                </label>
                <select
                  name="patient_id"
                  value={formData.patient_id}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-md ${
                    errors.patient_id ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">Select Patient</option>
                  {patients.map(patient => (
                    <option key={patient.id} value={patient.id}>
                      {patient.name} ({patient.patient_id})
                    </option>
                  ))}
                </select>
                {errors.patient_id && (
                  <p className="mt-1 text-sm text-red-500">{errors.patient_id}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Visit <span className="text-red-500">*</span>
                </label>
                <select
                  name="visit_id"
                  value={formData.visit_id}
                  onChange={handleChange}
                  disabled={!formData.patient_id}
                  className={`w-full px-3 py-2 border rounded-md ${
                    errors.visit_id ? 'border-red-500' : 'border-gray-300'
                  } ${!formData.patient_id ? 'bg-gray-100' : ''}`}
                >
                  <option value="">Select Visit</option>
                  {visits.map(visit => (
                    <option key={visit.id} value={visit.id}>
                      Visit #{visit.id} - {visit.date}
                    </option>
                  ))}
                </select>
                {errors.visit_id && (
                  <p className="mt-1 text-sm text-red-500">{errors.visit_id}</p>
                )}
              </div>
            </>
          )}

          {issueType === 'employee' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Employee <span className="text-red-500">*</span>
              </label>
              <select
                name="employee_id"
                value={formData.employee_id}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-md ${
                  errors.employee_id ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Select Employee</option>
                {employees.map(employee => (
                  <option key={employee.id} value={employee.id}>
                    {employee.name} ({employee.employee_id})
                  </option>
                ))}
              </select>
              {errors.employee_id && (
                <p className="mt-1 text-sm text-red-500">{errors.employee_id}</p>
              )}
            </div>
          )}

          {issueType === 'controlled' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Patient <span className="text-red-500">*</span>
                </label>
                <select
                  name="patient_id"
                  value={formData.patient_id}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-md ${
                    errors.patient_id ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">Select Patient</option>
                  {patients.map(patient => (
                    <option key={patient.id} value={patient.id}>
                      {patient.name} ({patient.patient_id})
                    </option>
                  ))}
                </select>
                {errors.patient_id && (
                  <p className="mt-1 text-sm text-red-500">{errors.patient_id}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Visit <span className="text-red-500">*</span>
                </label>
                <select
                  name="visit_id"
                  value={formData.visit_id}
                  onChange={handleChange}
                  disabled={!formData.patient_id}
                  className={`w-full px-3 py-2 border rounded-md ${
                    errors.visit_id ? 'border-red-500' : 'border-gray-300'
                  } ${!formData.patient_id ? 'bg-gray-100' : ''}`}
                >
                  <option value="">Select Visit</option>
                  {visits.map(visit => (
                    <option key={visit.id} value={visit.id}>
                      Visit #{visit.id} - {visit.date}
                    </option>
                  ))}
                </select>
                {errors.visit_id && (
                  <p className="mt-1 text-sm text-red-500">{errors.visit_id}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Prescription ID <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="prescription_id"
                  value={formData.prescription_id}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-md ${
                    errors.prescription_id ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.prescription_id && (
                  <p className="mt-1 text-sm text-red-500">{errors.prescription_id}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Authorized By (ID) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="authorized_by"
                  value={formData.authorized_by}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-md ${
                    errors.authorized_by ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.authorized_by && (
                  <p className="mt-1 text-sm text-red-500">{errors.authorized_by}</p>
                )}
              </div>
            </>
          )}

          {/* Common Fields */}
          <div className="col-span-2">
            <hr className="my-4" />
            <h3 className="text-lg font-medium text-gray-800 mb-3">
              Clinical Information
            </h3>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reference Type
            </label>
            <select
              name="reference_type"
              value={formData.reference_type}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            >
              <option value="consultation">Consultation</option>
              <option value="emergency">Emergency</option>
              <option value="followup">Follow-up</option>
              <option value="referral">Referral</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Purpose <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="purpose"
              value={formData.purpose}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-md ${
                errors.purpose ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.purpose && (
              <p className="mt-1 text-sm text-red-500">{errors.purpose}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Diagnosis <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="diagnosis"
              value={formData.diagnosis}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-md ${
                errors.diagnosis ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.diagnosis && (
              <p className="mt-1 text-sm text-red-500">{errors.diagnosis}</p>
            )}
          </div>

          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Doctor's Notes
            </label>
            <textarea
              name="doctor_notes"
              value={formData.doctor_notes}
              onChange={handleChange}
              rows="2"
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              placeholder="e.g., Take after food, 3 times daily for 3 days"
            />
          </div>

          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Remarks
            </label>
            <textarea
              name="remarks"
              value={formData.remarks}
              onChange={handleChange}
              rows="2"
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              placeholder="Additional remarks..."
            />
          </div>

          {/* Stock Warning */}
          {availableStock && parseInt(formData.quantity) > availableStock * 0.8 && (
            <div className="col-span-2">
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                <p className="text-yellow-700">
                  Warning: Issuing {formData.quantity} units will reduce stock to{' '}
                  {availableStock - parseInt(formData.quantity)} units (below 20% of current stock)
                </p>
              </div>
            </div>
          )}

          {/* Form Actions */}
          <div className="col-span-2">
            <div className="flex justify-end space-x-3 mt-4">
              <button
                type="button"
                onClick={handleReset}
                disabled={isLoading}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Reset
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center"
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Submitting...
                  </>
                ) : (
                  'Issue Stock'
                )}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default StockIssueForm;